import {
  DataGrid,
  DataGridProps,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid"
import { t } from "i18next"
import { FC, useCallback, useState } from "react"

type Sl2DataGridProps = {
  isLoading?: boolean
  datagridProps: Partial<DataGridProps>
  onNextTokenChange?: () => void
  listCount: number
  pageSize?: number
  /**
   * LOCKER-323 — opt-in backend-driven mode. When true, pagination + sorting
   * are controlled by the parent (server-side) and the nextToken-on-scroll
   * behavior is bypassed. Leaving it false preserves the original behavior for
   * every existing caller.
   */
  serverMode?: boolean
  rowCount?: number
  paginationModel?: GridPaginationModel
  onPaginationModelChange?: (model: GridPaginationModel) => void
  sortModel?: GridSortModel
  onSortModelChange?: (model: GridSortModel) => void
  pageSizeOptions?: number[]
}

const baseSx = {
  backgroundColor: "white",
  // disable cell selection style
  ".MuiDataGrid-cell:focus": {
    outline: "none",
  },
  // pointer cursor on ALL rows
  "& .MuiDataGrid-row:hover": {
    cursor: "pointer",
  },
}

export const Sl2DataGrid: FC<Sl2DataGridProps> = (props) => {
  const {
    datagridProps,
    isLoading,
    onNextTokenChange,
    pageSize,
    serverMode,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    pageSizeOptions,
  } = props
  const [maxFetchedPages, setMaxFetchedPages] = useState<number>(0)

  // Legacy nextToken-on-scroll pagination (client-accumulate mode).
  const onScrollPaginationModelChange = useCallback(
    (newPaginationModel: GridPaginationModel) => {
      if (newPaginationModel.page > maxFetchedPages) {
        onNextTokenChange?.()
      }
      setMaxFetchedPages(Math.max(maxFetchedPages, newPaginationModel.page))
    },
    [maxFetchedPages, onNextTokenChange],
  )

  const localeText = {
    noRowsLabel: isLoading ? "Loading ..." : t("noData"),
    MuiTablePagination: {
      labelDisplayedRows: ({
        from,
        to,
        count,
      }: {
        from: number
        to: number
        count: number
      }) => `${from} - ${to} of ${count === Number.MAX_VALUE ? "many" : count}`,
    },
    ...datagridProps?.localeText,
  }

  const mergedSx = { ...baseSx, ...(datagridProps?.sx as object) }

  if (serverMode) {
    return (
      // @ts-ignore
      <DataGrid
        {...datagridProps}
        loading={isLoading}
        localeText={localeText}
        autoHeight
        rowSelection={false}
        sx={mergedSx}
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={pageSizeOptions ?? [25, 50, 100]}
      />
    )
  }

  return (
    // @ts-ignore
    <DataGrid
      {...datagridProps}
      loading={isLoading}
      localeText={localeText}
      autoHeight
      rowSelection={false}
      sx={mergedSx}
      initialState={{
        pagination: { paginationModel: { pageSize } },
      }}
      onPaginationModelChange={onScrollPaginationModelChange}
    />
  )
}

export default Sl2DataGrid
